import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { Box, IconButton, Typography, Chip } from '@mui/material';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { DataTable } from 'src/components/common/table/DataTable';
import { buildQueryParams } from 'src/components/common/table/dataTableQueryBuilder';
import {
	IColumn,
	ITableQueryState,
} from 'src/components/common/types/tableTypes';
import {
	getAnnotationTemplates,
	ANNOTATION_TEMPLATE_LOADING_KEY,
} from 'src/features/annotation_template/store/annotationTemplateThunks';
import {
	IQuestionnaireTemplate,
	IQuestionnaireTemplateTableProps,
} from 'src/features/questionnaire_template/domain/types';
import WithRoleAccess from 'src/routes/WithRoleAccess';
import { AppDispatch } from 'src/store';
import { formatTimestamp } from 'src/utils/dateUtils';

import { AnnotationTemplateTableActions } from './AnnotationTemplateTableActions';

export const AnnotationTemplateTable: React.FC<
	IQuestionnaireTemplateTableProps
> = ({ siteSlug }) => {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();

	const isLoading = useSelector(
		(state: { loading: ILoadingState }) =>
			state.loading.loadingStates[ANNOTATION_TEMPLATE_LOADING_KEY],
	);

	const [queryState, setQueryState] = useState<ITableQueryState>({
		sortBy: 'index',
		sortOrder: 'asc',
	});
	const [templates, setTemplates] = useState<IQuestionnaireTemplate[]>([]);
	const [clickedRow, setClickedRow] = useState<IQuestionnaireTemplate>();
	const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(
		null,
	);

	const handleMenuClick = (
		event: React.MouseEvent<HTMLElement>,
		annotationTemplate: IQuestionnaireTemplate,
	) => {
		setClickedRow(annotationTemplate);
		setAnchorElement(event.currentTarget);
	};

	const columns: IColumn<IQuestionnaireTemplate>[] = [
		{
			key: 'sn',
			label: 'S.N.',
			width: '7%',
			render: (_annotationTemplate, index) => index + 1,
		},
		{
			key: 'name',
			label: 'Template Name',
			render: (annotationTemplate) => annotationTemplate.name,
		},
		{
			key: 'index',
			label: 'Index',
			sortable: true,
			render: (annotationTemplate) => annotationTemplate.index,
		},
		{
			key: 'published',
			label: 'Published',
			render: (annotationTemplate) => (
				<Chip
					label={annotationTemplate.published ? 'Yes' : 'No'}
					variant='outlined'
					size='medium'
				/>
			),
		},
		{
			key: 'created_at',
			label: 'Created At',
			sortable: true,
			render: (annotationTemplate) =>
				formatTimestamp(annotationTemplate.created_at),
		},
		{
			key: 'actions',
			label: 'Actions',
			render: (annotationTemplate) => (
				<IconButton
					onClick={(e) => handleMenuClick(e, annotationTemplate)}
				>
					<MoreVertOutlinedIcon />
				</IconButton>
			),
		},
	];

	const handleMenuClose = () => {
		setAnchorElement(null);
		setClickedRow(undefined);
	};

	useEffect(() => {
		const params = buildQueryParams(queryState);

		dispatch(
			getAnnotationTemplates({
				requestParams: params,
				siteSlug: siteSlug,
				onSuccess: (response) => {
					setTemplates(response);
				},
			}),
		);
	}, [queryState, siteSlug]);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				padding: 3,
				backgroundColor: 'white',
				borderRadius: '4px',
				marginTop: '20px',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<Typography
					variant='h6'
					sx={{
						fontSize: '20px',
						fontWeight: 500,
						color: 'rgba(0, 0, 0, 0.87)',
					}}
				>
					Annotation Templates
				</Typography>

				<WithRoleAccess allowedSiteRoles={['super_admin', 'admin']}>
					<Box sx={{ width: '320px', height: '36px' }}>
						<PrimaryButton
							disabled={isLoading}
							onClick={() =>
								navigate(
									`/site/${siteSlug}/annotation-template-builder`,
									{
										state: {
											nextGeneratedIndex:
												templates.length + 1,
										},
									},
								)
							}
							sx={{
								height: '36px',
								minHeight: '36px',
								'& .MuiButton-root': {
									height: '36px',
									minHeight: '36px',
								},
								backgroundColor: '#BE3327',
								'&:hover': {
									backgroundColor: '#A52D23',
								},
							}}
						>
							Create Annotation Template
						</PrimaryButton>
					</Box>
				</WithRoleAccess>
			</Box>

			<DataTable
				columns={columns}
				data={templates}
				loadingKey={ANNOTATION_TEMPLATE_LOADING_KEY}
				emptyMessage='No annotation templates found'
				enablePagination={false}
				queryState={queryState}
				onQueryChange={(newQueryState) => {
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					const { page, ...rest } = newQueryState;
					setQueryState(rest);
				}}
			/>

			{clickedRow && (
				<AnnotationTemplateTableActions
					siteSlug={siteSlug}
					selectedRow={clickedRow}
					anchorElement={anchorElement}
					clearAnchorElement={() => setAnchorElement(null)}
					OnActionHit={() => setQueryState({ ...queryState })}
					onClose={handleMenuClose}
				/>
			)}
		</Box>
	);
};
